import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlaySquare, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type MediaItem = {
  id: string;
  title: string;
  description: string | null;
  published_at: string | null;
  thumbnail_url: string | null;
  source_url: string;
};

type FeedsResponse = {
  youtube: MediaItem[];
  spotify: MediaItem[];
  amazon: MediaItem[];
  cached?: boolean;
  fetched_at?: string;
  error?: string;
};

const formatDate = (s: string | null) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const SOURCES: Array<{
  key: "youtube" | "spotify" | "amazon";
  label: string;
  badgeClass: string;
}> = [
  { key: "youtube", label: "YouTube", badgeClass: "bg-red-600" },
  { key: "spotify", label: "Spotify", badgeClass: "bg-green-600" },
  { key: "amazon", label: "Amazon Music", badgeClass: "bg-sky-600" },
];

export const MediaScroller = () => {
  const { data, isLoading, error } = useQuery<FeedsResponse>({
    queryKey: ["media-feeds"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<FeedsResponse>(
        "fetch-media-feeds",
        { method: "GET" }
      );
      if (error) throw error;
      if (!data) throw new Error("No data");
      return data;
    },
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlaySquare className="h-5 w-5" />
          Latest Media
        </CardTitle>
        <CardDescription>
          Recent videos and podcast episodes from our YouTube, Spotify and Amazon Music feeds
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="youtube">
          <TabsList>
            {SOURCES.map((s) => (
              <TabsTrigger key={s.key} value={s.key}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {SOURCES.map((s) => {
            const items = (data?.[s.key] ?? []).slice(0, 20);
            return (
              <TabsContent key={s.key} value={s.key} className="mt-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Could not load feed. Please try again later.
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No {s.label} entries available
                  </div>
                ) : (
                  <ScrollArea className="w-full whitespace-nowrap">
                    <div className="flex gap-4 pb-4">
                      {items.map((item) => (
                        <a
                          key={item.id}
                          href={item.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-[280px] flex-shrink-0 group"
                        >
                          <Card className="h-full hover:shadow-md transition-shadow border hover:border-primary/30 overflow-hidden">
                            {item.thumbnail_url ? (
                              <div className="h-40 overflow-hidden bg-muted">
                                <img
                                  src={item.thumbnail_url}
                                  alt={item.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            ) : (
                              <div className="h-40 bg-muted flex items-center justify-center">
                                <PlaySquare className="h-10 w-10 text-muted-foreground" />
                              </div>
                            )}
                            <CardContent className="p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className={`text-[10px] uppercase tracking-wide font-medium text-white px-2 py-0.5 rounded ${s.badgeClass}`}
                                >
                                  {s.label}
                                </span>
                                {item.published_at && (
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(item.published_at)}
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-sm line-clamp-2 whitespace-normal group-hover:text-primary transition-colors">
                                {item.title}
                              </h3>
                              {item.description && (
                                <p className="text-xs text-muted-foreground mt-2 line-clamp-2 whitespace-normal">
                                  {item.description}
                                </p>
                              )}
                              <div className="mt-3 flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                Open in {s.label} <ExternalLink className="h-3 w-3" />
                              </div>
                            </CardContent>
                          </Card>
                        </a>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};
